/**
 * Lightweight upload-prompt modal shown when the user clicks Editor or Crop
 * before adding any image. Lets them drop a file or click to browse without
 * leaving the action they intended — once a file lands, the prompt closes
 * and the caller's onResolved() fires so the caller can open its own dialog
 * with the freshly-added source selected.
 */

import { createDropZone } from './DropZone';
import type { QueueStore } from '@/lib/queue/store';

export interface UploadPromptOptions {
  /** Title shown in the modal header — e.g. "Open in Editor" or "Open in Crop". */
  title: string;
  /** Sub-message shown above the drop zone. */
  message?: string;
}

/** Show the upload prompt and resolve once a file is added (or null if cancelled). */
export function promptForUpload(
  store: QueueStore,
  opts: UploadPromptOptions,
): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'rd-modal-backdrop upload-prompt-backdrop';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-label', opts.title);

    const dialog = document.createElement('div');
    dialog.className = 'rd-modal upload-prompt-dialog';

    const header = document.createElement('div');
    header.className = 'rd-modal__header';
    const title = document.createElement('h2');
    title.className = 'rd-modal__title';
    title.textContent = opts.title;
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'rd-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    header.append(title, closeBtn);

    const content = document.createElement('div');
    content.className = 'rd-modal__content upload-prompt-content';

    if (opts.message) {
      const msg = document.createElement('p');
      msg.className = 'upload-prompt-msg';
      msg.textContent = opts.message;
      content.appendChild(msg);
    }

    let resolved = false;
    function close(success: boolean): void {
      if (resolved) return;
      resolved = true;
      if (backdrop.parentElement) backdrop.parentElement.removeChild(backdrop);
      document.removeEventListener('keydown', onEsc);
      resolve(success);
    }

    const dropZone = createDropZone((files) => {
      if (!files || files.length === 0) return;
      store.addFiles(files);
      // Confirm at least one file was actually accepted (filtered by mime).
      const hasSource = store.getState().items.some(i => i.isSource);
      close(hasSource);
    });
    content.appendChild(dropZone);

    dialog.append(header, content);
    backdrop.appendChild(dialog);

    closeBtn.addEventListener('click', () => close(false));
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(false); });
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') close(false);
    }
    document.addEventListener('keydown', onEsc);

    document.body.appendChild(backdrop);
  });
}
